import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, formatApiErrorDetail } from '../utils/api';
import { Heart, Sparkles, MessageCircle, ShieldCheck, Send, ChevronDown, ChevronUp, Lock, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';

const KIND_META = {
  question: { label: 'Question of the Week', accent: '#E57A44', emojiFallback: '💭' },
  poll: { label: 'Connection Poll', accent: '#5B7C99', emojiFallback: '🗳️' },
  challenge: { label: 'Mini Challenge', accent: '#78A18E', emojiFallback: '🌱' },
  sentence: { label: 'Finish the Sentence', accent: '#E8C265', emojiFallback: '✍️' },
  scenario: { label: 'Relationship Scenario', accent: '#B85C7E', emojiFallback: '🎭' },
};

const REACTIONS = ['❤️', '🙏', '💡', '💬', '✨', '🕊️'];

export default function ConnectionRoom() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(false);
  const [payload, setPayload] = useState({ theme: null, items: [], published_answers: [], disclaimer: '' });
  const [drafts, setDrafts] = useState({}); // item_id -> text
  const [savingItem, setSavingItem] = useState(null);
  const [showAnonForm, setShowAnonForm] = useState(false);
  const [anonText, setAnonText] = useState('');
  const [anonCategory, setAnonCategory] = useState('');
  const [anonNotify, setAnonNotify] = useState(false);
  const [anonSubmitting, setAnonSubmitting] = useState(false);
  const [openThread, setOpenThread] = useState(null); // answer_id
  const [threadData, setThreadData] = useState({}); // answer_id -> { answer, reactions, replies }
  const [replyDraft, setReplyDraft] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/connection-room/current');
      setPayload(data);
      setGated(false);
    } catch (err) {
      if (err?.response?.status === 403) {
        setGated(true);
      } else {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail) || 'Could not load the room');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      nav('/login?redirect=/sanctuary/connection-room');
      return;
    }
    load();
  }, [user, load, nav]);

  const submitReflection = async (item) => {
    const text = (drafts[item.id] || '').trim();
    if (!text) {
      toast.error('Write a short reflection first');
      return;
    }
    setSavingItem(item.id);
    try {
      await api.post('/connection-room/reflections', { item_id: item.id, text });
      toast.success('Your reflection was saved — thank you for sharing');
      setDrafts((prev) => ({ ...prev, [item.id]: '' }));
      setPayload((prev) => ({
        ...prev,
        items: prev.items.map((i) => (i.id === item.id ? { ...i, already_reflected: true } : i)),
      }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || 'Could not save');
    } finally {
      setSavingItem(null);
    }
  };

  const castVote = async (item, option) => {
    // Optimistic update
    setPayload((prev) => ({
      ...prev,
      items: prev.items.map((i) => {
        if (i.id !== item.id) return i;
        const prevChoice = i.my_vote;
        const newOptions = (i.poll_options || []).map((o) => {
          let votes = o.votes || 0;
          if (o.id === option.id) votes = votes + (prevChoice === option.id ? 0 : 1);
          if (o.id === prevChoice && prevChoice !== option.id) votes = Math.max(0, votes - 1);
          return { ...o, votes };
        });
        const total = newOptions.reduce((s, o) => s + (o.votes || 0), 0);
        return { ...i, poll_options: newOptions, my_vote: option.id, total_votes: total };
      }),
    }));
    try {
      await api.post(`/connection-room/items/${item.id}/vote`, { option_id: option.id });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || 'Vote failed');
      load();
    }
  };

  const submitAnonQuestion = async () => {
    const text = anonText.trim();
    if (text.length < 5) {
      toast.error('Add a bit more detail');
      return;
    }
    setAnonSubmitting(true);
    try {
      await api.post('/connection-room/anon-questions', {
        question: text,
        category: anonCategory.trim() || null,
        notify_email: anonNotify,
      });
      toast.success("Sent to Genesis — thank you");
      setAnonText('');
      setAnonCategory('');
      setAnonNotify(false);
      setShowAnonForm(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || 'Could not submit');
    } finally {
      setAnonSubmitting(false);
    }
  };

  const openAnswer = async (answer_id) => {
    if (openThread === answer_id) {
      setOpenThread(null);
      return;
    }
    setOpenThread(answer_id);
    setReplyDraft('');
    if (!threadData[answer_id]) {
      try {
        const { data } = await api.get(`/connection-room/answers/${answer_id}/thread`);
        setThreadData((prev) => ({ ...prev, [answer_id]: data }));
      } catch (err) {
        toast.error('Could not load conversation');
      }
    }
  };

  const toggleReaction = async (answer_id, emoji) => {
    // Optimistic
    setThreadData((prev) => {
      const t = prev[answer_id];
      if (!t) return prev;
      const existing = t.reactions.find((r) => r.emoji === emoji);
      let next;
      if (existing) {
        const mine = existing.mine;
        const count = Math.max(0, existing.count + (mine ? -1 : 1));
        if (count === 0) next = t.reactions.filter((r) => r.emoji !== emoji);
        else next = t.reactions.map((r) => (r.emoji === emoji ? { ...r, count, mine: !mine } : r));
      } else {
        next = [...t.reactions, { emoji, count: 1, mine: true }];
      }
      return { ...prev, [answer_id]: { ...t, reactions: next } };
    });
    try {
      await api.post(`/connection-room/answers/${answer_id}/react`, { emoji });
    } catch {
      // On error refetch
      const { data } = await api.get(`/connection-room/answers/${answer_id}/thread`);
      setThreadData((prev) => ({ ...prev, [answer_id]: data }));
    }
  };

  const postReply = async (answer_id, parent_reply_id = null) => {
    const text = replyDraft.trim();
    if (!text) return;
    try {
      const { data } = await api.post(`/connection-room/answers/${answer_id}/replies`, {
        text,
        parent_reply_id,
      });
      setThreadData((prev) => {
        const t = prev[answer_id];
        if (!t) return prev;
        return { ...prev, [answer_id]: { ...t, replies: [...t.replies, data.reply] } };
      });
      setReplyDraft('');
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || 'Reply failed');
    }
  };

  const deleteReply = async (answer_id, reply_id) => {
    if (!window.confirm('Delete this reply?')) return;
    try {
      await api.delete(`/connection-room/replies/${reply_id}`);
      setThreadData((prev) => {
        const t = prev[answer_id];
        if (!t) return prev;
        return { ...prev, [answer_id]: { ...t, replies: t.replies.filter((r) => r.id !== reply_id) } };
      });
    } catch (err) {
      toast.error('Could not delete');
    }
  };

  // ---------- gated view ----------
  if (loading && !gated) {
    return (
      <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
        <Header />
        <div className="max-w-4xl mx-auto px-6 py-16 text-center" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
          Loading the Connection Room…
        </div>
        <Footer />
      </div>
    );
  }
  if (gated) {
    return (
      <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="connection-room-gated">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6" style={{ backgroundColor: '#E8C26525' }}>
            <Lock className="w-9 h-9" style={{ color: '#8B6A0A' }} />
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold mb-3" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>
            The Connection Room lives inside The Sanctuary
          </h1>
          <p className="mb-8 text-base" style={{ fontFamily: 'Lora, serif', color: '#5C564D', lineHeight: 1.8 }}>
            This is a private, member-only space for reflection, community polls, weekly challenges, and anonymous questions Genesis answers personally. Join The Sanctuary to step inside.
          </p>
          <Link
            to="/sanctuary"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-full transition-all duration-300 hover:-translate-y-0.5"
            style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 700 }}
            data-testid="connection-room-join-sanctuary"
          >
            Learn about The Sanctuary
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const { theme, items, published_answers, disclaimer } = payload;

  return (
    <div style={{ position: 'relative', minHeight: '100vh', overflow: 'hidden' }}>
      {/* Serene aurora backdrop — warm cream, soft peach, whisper of teal */}
      <div
        aria-hidden
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 0,
          background: 'radial-gradient(1200px 700px at 15% -10%, rgba(232,194,101,0.28), transparent 60%), radial-gradient(1000px 800px at 100% 10%, rgba(229,122,68,0.20), transparent 55%), radial-gradient(900px 700px at 50% 110%, rgba(120,161,142,0.18), transparent 60%), linear-gradient(180deg, #FDFBF7 0%, #F7EFE1 55%, #FDFBF7 100%)',
        }}
      />
      {/* Floating specks for gentle life */}
      <div aria-hidden style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', top: '18%', left: '8%', width: 6, height: 6, borderRadius: '50%', background: '#E57A44', opacity: 0.35, animation: 'crFloat 12s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', top: '38%', right: '12%', width: 8, height: 8, borderRadius: '50%', background: '#78A18E', opacity: 0.35, animation: 'crFloat 15s ease-in-out infinite reverse' }} />
        <div style={{ position: 'absolute', bottom: '20%', left: '18%', width: 5, height: 5, borderRadius: '50%', background: '#E8C265', opacity: 0.5, animation: 'crFloat 10s ease-in-out infinite' }} />
      </div>
      <style>{`
        @keyframes crFloat { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-14px) } }
        @keyframes crRise { from { opacity: 0; transform: translateY(12px) } to { opacity: 1; transform: none } }
        .cr-rise { animation: crRise 500ms cubic-bezier(.2,.8,.2,1) both }
      `}</style>

      <div style={{ position: 'relative', zIndex: 1 }}>
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        {/* Hero + theme */}
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-4 text-xs"
            style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#78A18E15', color: '#4B6B5C', fontWeight: 700, letterSpacing: '0.08em' }}>
            <Sparkles className="w-3 h-3" /> SANCTUARY · CONNECTION ROOM
          </div>
          <h1 className="text-4xl sm:text-5xl font-semibold mb-3" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724', letterSpacing: '-0.015em' }}>
            The Connection Room
          </h1>
          <p className="max-w-xl mx-auto text-base" style={{ fontFamily: 'Lora, serif', color: '#5C564D', lineHeight: 1.8, fontStyle: 'italic' }}>
            Pause, reflect, connect, and practice healthier relationships throughout the week.
          </p>
        </div>

        {theme && (
          <div
            className="mb-8 p-6 rounded-3xl relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #FFF7E6 0%, #F3E3B8 100%)',
              border: '1px solid rgba(232, 194, 101, 0.35)',
            }}
            data-testid="connection-room-theme"
          >
            <p className="text-xs font-bold uppercase mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#8B6A0A', letterSpacing: '0.14em' }}>
              This Month's Theme
            </p>
            <div className="flex items-start gap-3">
              {theme.emoji && <span className="text-4xl">{theme.emoji}</span>}
              <div>
                <h2 className="text-2xl font-semibold mb-1" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>
                  {theme.title}
                </h2>
                {theme.description && (
                  <p className="text-sm" style={{ fontFamily: 'Lora, serif', color: '#5C4515', lineHeight: 1.7 }}>
                    {theme.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Items */}
        {items.length === 0 && (
          <div className="p-10 rounded-3xl text-center" style={{ backgroundColor: '#FFFFFF', border: '1px solid #EFEAE0' }} data-testid="connection-room-empty">
            <p style={{ fontFamily: 'Lora, serif', color: '#595553', lineHeight: 1.8 }}>
              Genesis is preparing this week's reflections. Check back soon — something meaningful is on the way.
            </p>
          </div>
        )}

        <div className="space-y-6">
          {items.map((item, idx) => {
            const meta = KIND_META[item.kind] || KIND_META.question;
            const emoji = item.emoji || meta.emojiFallback;
            return (
              <article
                key={item.id}
                className="p-6 rounded-3xl cr-rise"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.85)',
                  backdropFilter: 'blur(6px)',
                  border: '1px solid rgba(239, 234, 224, 0.9)',
                  boxShadow: '0 10px 40px rgba(44,39,36,0.06)',
                  animationDelay: `${idx * 80}ms`,
                }}
                data-testid={`connection-item-${item.id}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span
                    className="text-xs font-bold uppercase px-2 py-0.5 rounded-full"
                    style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: `${meta.accent}15`, color: meta.accent, letterSpacing: '0.1em' }}
                  >
                    {meta.label}
                  </span>
                </div>
                <div className="flex items-start gap-3 mb-4">
                  <span className="text-3xl leading-none">{emoji}</span>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-semibold" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724', letterSpacing: '-0.01em' }}>
                      {item.title}
                    </h3>
                    {item.prompt && (
                      <p className="mt-2 text-sm sm:text-base" style={{ fontFamily: 'Lora, serif', color: '#5C564D', lineHeight: 1.7 }}>
                        {item.prompt}
                      </p>
                    )}
                  </div>
                </div>

                {item.image_url && (
                  <img
                    src={item.image_url}
                    alt=""
                    className="w-full rounded-2xl mb-4"
                    style={{ maxHeight: 320, objectFit: 'cover' }}
                  />
                )}

                {item.kind === 'poll' ? (
                  <div className="space-y-2" data-testid={`poll-${item.id}`}>
                    {(item.poll_options || []).map((opt) => {
                      const total = item.total_votes || 0;
                      const pct = total ? Math.round(((opt.votes || 0) / total) * 100) : 0;
                      const isMine = item.my_vote === opt.id;
                      return (
                        <button
                          key={opt.id}
                          onClick={() => castVote(item, opt)}
                          className="w-full text-left rounded-2xl p-3 relative overflow-hidden transition"
                          style={{
                            fontFamily: 'Nunito, sans-serif',
                            backgroundColor: '#FDFBF7',
                            border: `1.5px solid ${isMine ? meta.accent : '#EFEAE0'}`,
                            color: '#2C2724',
                          }}
                          data-testid={`poll-option-${opt.id}`}
                        >
                          <div
                            className="absolute inset-y-0 left-0 rounded-2xl transition-all duration-500"
                            style={{ width: `${pct}%`, backgroundColor: `${meta.accent}18` }}
                          />
                          <div className="relative flex items-center gap-2 justify-between">
                            <span className="flex items-center gap-2 text-sm font-semibold">
                              {opt.emoji && <span>{opt.emoji}</span>}
                              <span>{opt.text}</span>
                              {isMine && <span className="text-xs" style={{ color: meta.accent }}>Your vote</span>}
                            </span>
                            <span className="text-xs font-bold" style={{ color: '#5C564D' }}>{pct}%</span>
                          </div>
                        </button>
                      );
                    })}
                    <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                      {item.total_votes || 0} {item.total_votes === 1 ? 'vote' : 'votes'} so far · tap to change
                    </p>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <ShieldCheck className="w-4 h-4" style={{ color: '#78A18E' }} />
                      <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#5C564D' }}>
                        Your reflection stays private — only Genesis sees it.
                      </p>
                    </div>
                    <textarea
                      value={drafts[item.id] || ''}
                      onChange={(e) => setDrafts((p) => ({ ...p, [item.id]: e.target.value }))}
                      placeholder={item.already_reflected ? 'Update your reflection…' : 'Write from where you are…'}
                      rows={3}
                      maxLength={5000}
                      className="w-full rounded-2xl p-3 focus:outline-none resize-none"
                      style={{ fontFamily: 'Lora, serif', color: '#2C2724', backgroundColor: '#FDFBF7', border: '1px solid #EFEAE0', lineHeight: 1.7 }}
                      data-testid={`reflection-input-${item.id}`}
                    />
                    <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
                      <button
                        onClick={() => submitReflection(item)}
                        disabled={savingItem === item.id}
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm disabled:opacity-60"
                        style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: meta.accent, color: '#FFFFFF', fontWeight: 700 }}
                        data-testid={`reflection-submit-${item.id}`}
                      >
                        <Send className="w-4 h-4" />
                        {savingItem === item.id ? 'Saving…' : item.already_reflected ? 'Update reflection' : 'Save reflection'}
                      </button>
                      {item.already_reflected && (
                        <span className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#78A18E' }}>
                          ✓ Already reflected this week
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>

        {/* Anonymous Question Box */}
        <div
          className="mt-10 p-6 rounded-3xl"
          style={{ background: 'linear-gradient(135deg, #FDF6EE 0%, #F4E7D3 100%)', border: '1px solid #E0C89A' }}
          data-testid="anon-question-box"
        >
          <div className="flex items-center gap-3 mb-3">
            <span className="text-3xl">📩</span>
            <div className="flex-1">
              <h3 className="text-lg font-semibold" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>
                Anonymous Question Box
              </h3>
              <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#5C4515' }}>
                Ask about relationships, family, friendships, self-growth, or faith. Genesis picks one or two to answer each month.
              </p>
            </div>
            <button
              onClick={() => setShowAnonForm((s) => !s)}
              className="px-3 py-1.5 rounded-full text-xs"
              style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#8B6A0A', color: '#FFF', fontWeight: 700 }}
              data-testid="anon-toggle"
            >
              {showAnonForm ? 'Close' : 'Ask a question'}
            </button>
          </div>
          {showAnonForm && (
            <div className="mt-3 space-y-3">
              <input
                value={anonCategory}
                onChange={(e) => setAnonCategory(e.target.value)}
                placeholder="Topic (optional) — e.g. Marriage, Family, Self-growth"
                maxLength={80}
                className="w-full rounded-xl p-3 text-sm focus:outline-none"
                style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FFFFFF', border: '1px solid #E0C89A', color: '#2C2724' }}
                data-testid="anon-category"
              />
              <textarea
                value={anonText}
                onChange={(e) => setAnonText(e.target.value)}
                placeholder="Your question stays anonymous — no one sees who you are."
                rows={4}
                maxLength={2000}
                className="w-full rounded-xl p-3 text-sm focus:outline-none resize-none"
                style={{ fontFamily: 'Lora, serif', backgroundColor: '#FFFFFF', border: '1px solid #E0C89A', color: '#2C2724', lineHeight: 1.7 }}
                data-testid="anon-input"
              />
              <label className="flex items-center gap-2 text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#5C4515' }}>
                <input type="checkbox" checked={anonNotify} onChange={(e) => setAnonNotify(e.target.checked)} data-testid="anon-notify" />
                Email me when Genesis answers (your email won't be shared)
              </label>
              <button
                onClick={submitAnonQuestion}
                disabled={anonSubmitting}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm disabled:opacity-60"
                style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#8B6A0A', color: '#FFF', fontWeight: 700 }}
                data-testid="anon-submit"
              >
                <Send className="w-4 h-4" /> Send anonymously
              </button>
            </div>
          )}
        </div>

        {/* Published answers */}
        {published_answers.length > 0 && (
          <div className="mt-10">
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>
              Genesis answers
            </h2>
            <div className="space-y-4">
              {published_answers.map((a) => {
                const t = threadData[a.id];
                const open = openThread === a.id;
                return (
                  <div key={a.id} className="p-5 rounded-2xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #EFEAE0' }} data-testid={`answer-${a.id}`}>
                    {a.category && (
                      <p className="text-xs uppercase font-bold mb-1" style={{ fontFamily: 'Manrope, sans-serif', color: '#78A18E', letterSpacing: '0.1em' }}>
                        {a.category}
                      </p>
                    )}
                    <p className="text-sm italic mb-2" style={{ fontFamily: 'Lora, serif', color: '#5C564D', lineHeight: 1.7 }}>
                      Someone asked: {a.question}
                    </p>
                    <div className="pl-4 mt-3" style={{ borderLeft: '2px solid #E57A44' }}>
                      <p className="text-sm" style={{ fontFamily: 'Lora, serif', color: '#2C2724', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                        {a.answer}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {REACTIONS.map((emoji) => {
                        const react = t?.reactions?.find((r) => r.emoji === emoji);
                        const count = react?.count || 0;
                        const mine = react?.mine;
                        return (
                          <button
                            key={emoji}
                            onClick={() => (open ? toggleReaction(a.id, emoji) : openAnswer(a.id))}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs transition"
                            style={{
                              fontFamily: 'Manrope, sans-serif',
                              backgroundColor: mine ? '#E57A4415' : '#FDFBF7',
                              border: `1px solid ${mine ? '#E57A44' : '#EFEAE0'}`,
                              color: '#2C2724',
                              fontWeight: 600,
                            }}
                            data-testid={`reaction-${a.id}-${emoji}`}
                          >
                            <span>{emoji}</span>
                            {count > 0 && <span>{count}</span>}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => openAnswer(a.id)}
                        className="ml-auto inline-flex items-center gap-1 text-xs"
                        style={{ fontFamily: 'Manrope, sans-serif', color: '#E57A44', fontWeight: 700 }}
                        data-testid={`toggle-thread-${a.id}`}
                      >
                        {open ? <><ChevronUp className="w-4 h-4" /> Hide conversation</> : <><ChevronDown className="w-4 h-4" /> Join the conversation</>}
                      </button>
                    </div>

                    {open && t && (
                      <div className="mt-4 pt-4" style={{ borderTop: '1px solid #EFEAE0' }}>
                        {(t.replies || []).length === 0 && (
                          <p className="text-xs mb-3" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D', fontStyle: 'italic' }}>
                            Be the first to add a reflection to this conversation.
                          </p>
                        )}
                        <div className="space-y-3 mb-3">
                          {(t.replies || []).map((r) => (
                            <div key={r.id} className="p-3 rounded-xl" style={{ backgroundColor: '#FDFBF7', border: '1px solid #EFEAE0' }} data-testid={`reply-${r.id}`}>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: r.is_founder ? '#E57A44' : '#2C2724' }}>
                                  {r.user_name}
                                  {r.is_founder && <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px]" style={{ backgroundColor: '#E57A4420', color: '#E57A44' }}>FOUNDER</span>}
                                </span>
                                {(r.user_id === user?._id || user?.role === 'admin') && (
                                  <button
                                    onClick={() => deleteReply(a.id, r.id)}
                                    className="ml-auto text-xs opacity-60 hover:opacity-100"
                                    style={{ color: '#B85C7E' }}
                                    aria-label="Delete reply"
                                    data-testid={`delete-reply-${r.id}`}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                              <p className="text-sm" style={{ fontFamily: 'Lora, serif', color: '#2C2724', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                                {r.text}
                              </p>
                            </div>
                          ))}
                        </div>
                        <textarea
                          value={replyDraft}
                          onChange={(e) => setReplyDraft(e.target.value)}
                          placeholder="Share your thoughts…"
                          rows={2}
                          maxLength={2000}
                          className="w-full rounded-xl p-3 text-sm focus:outline-none resize-none"
                          style={{ fontFamily: 'Lora, serif', backgroundColor: '#FDFBF7', border: '1px solid #EFEAE0', color: '#2C2724', lineHeight: 1.6 }}
                          data-testid={`reply-input-${a.id}`}
                        />
                        <button
                          onClick={() => postReply(a.id)}
                          className="mt-2 inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs"
                          style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FFF', fontWeight: 700 }}
                          data-testid={`reply-submit-${a.id}`}
                        >
                          <MessageCircle className="w-3.5 h-3.5" /> Post
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Disclaimer */}
        {disclaimer && (
          <div className="mt-12 p-5 rounded-2xl text-center" style={{ backgroundColor: '#F3EFE7', border: '1px solid #E6E2D8' }} data-testid="connection-room-disclaimer">
            <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#5C564D', lineHeight: 1.7 }}>
              {disclaimer}
            </p>
          </div>
        )}
      </div>
      <Footer />
      </div>
    </div>
  );
}
